import { useCallback, useEffect, useState } from 'react';
import { readStoreConsent, writeStoreConsent } from '../../tracking/storeConsent.js';
import {
  initGa4,
  initMetaPixel,
  resetStoreTrackingRuntime,
  trackPageView,
} from '../../tracking/storeTracking.js';

/**
 * @param {{
 *   slug: string,
 *   ga4MeasurementId: string,
 *   metaPixelId: string,
 *   tenantName?: string,
 * }} props
 */
export function StoreConsentBanner({ slug, ga4MeasurementId, metaPixelId, tenantName = 'esta loja' }) {
  const needsConsent = Boolean(ga4MeasurementId || metaPixelId);
  const [consent, setConsent] = useState(() => readStoreConsent(slug));
  const [visible, setVisible] = useState(() => needsConsent && !readStoreConsent(slug).decided);

  useEffect(() => {
    setConsent(readStoreConsent(slug));
    setVisible(needsConsent && !readStoreConsent(slug).decided);
  }, [slug, needsConsent]);

  const activateTracking = useCallback(
    (choice) => {
      writeStoreConsent(slug, choice);
      setConsent({ ...choice, decided: true });
      setVisible(false);

      if (choice.analytics && ga4MeasurementId) {
        initGa4(ga4MeasurementId, choice);
      }
      if (choice.marketing && metaPixelId) {
        initMetaPixel(metaPixelId);
      }
      if (choice.analytics) {
        trackPageView(true, {
          page_path: window.location.pathname + window.location.search,
          page_title: document.title,
        });
      }
    },
    [slug, ga4MeasurementId, metaPixelId],
  );

  const acceptAll = useCallback(() => {
    activateTracking({ analytics: true, marketing: true });
  }, [activateTracking]);

  const rejectAll = useCallback(() => {
    writeStoreConsent(slug, { analytics: false, marketing: false });
    setConsent({ analytics: false, marketing: false, decided: true });
    setVisible(false);
    resetStoreTrackingRuntime();
  }, [slug]);

  useEffect(() => {
    if (!needsConsent || !consent.decided) return;
    if (consent.analytics && ga4MeasurementId) {
      initGa4(ga4MeasurementId, consent);
    }
    if (consent.marketing && metaPixelId) {
      initMetaPixel(metaPixelId);
    }
  }, [needsConsent, consent, ga4MeasurementId, metaPixelId]);

  if (!needsConsent || !visible) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="store-consent-title"
      aria-describedby="store-consent-desc"
      className="fixed inset-x-0 bottom-0 z-[200] border-t border-zinc-200/90 bg-white/95 px-4 py-4 shadow-[0_-8px_32px_rgba(0,0,0,0.12)] backdrop-blur-md dark:border-zinc-700/80 dark:bg-zinc-950/95 sm:bottom-4 sm:mx-auto sm:max-w-2xl sm:rounded-2xl sm:border"
    >
      <p id="store-consent-title" className="text-sm font-bold text-zinc-900 dark:text-zinc-50">
        Cookies e privacidade
      </p>
      <p id="store-consent-desc" className="mt-1.5 text-xs leading-relaxed text-zinc-600 dark:text-zinc-400">
        {tenantName} utiliza Google Analytics e Meta Pixel para medir visitas e campanhas (remarketing), apenas com o
        seu consentimento. Pode recusar — a loja continua a funcionar normalmente.
      </p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={rejectAll}
          className="rounded-xl border border-zinc-200 px-4 py-2.5 text-xs font-semibold text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800"
        >
          Apenas essenciais
        </button>
        <button
          type="button"
          onClick={acceptAll}
          className="rounded-xl bg-[var(--st-600)] px-4 py-2.5 text-xs font-bold text-white shadow-sm transition hover:bg-[var(--st-700)]"
        >
          Aceitar análise e marketing
        </button>
      </div>
    </div>
  );
}

/** @param {string} slug */
export function useStoreConsent(slug) {
  const [consent, setConsent] = useState(() => readStoreConsent(slug));

  useEffect(() => {
    setConsent(readStoreConsent(slug));
  }, [slug]);

  useEffect(() => {
    if (!slug || typeof window === 'undefined') return undefined;
    function onStorage(ev) {
      if (ev.key === `ominia.consent.v2.${slug}`) {
        setConsent(readStoreConsent(slug));
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [slug]);

  return consent;
}
